import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import CPFMaskInput from '../masks/CPFMaskInput';
import { unformatCPF, formatCPF, isValidCPF } from '../../utils/cpfUtils';

const RequisitanteModal = ({ isOpen, requisitante, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    cnpj: '',
    num: '',
    nome: '',
    responsavelNome: '',
    responsavelFuncao: '',
    responsavelMatricula: '',
    responsavelCpf: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const token = localStorage.getItem('token');
  const cnpjFromStorage = localStorage.getItem('cnpj');

  // Atualiza formData ao abrir modal ou mudar requisitante
  useEffect(() => {
    if (isOpen) {
      if (requisitante) {
        setFormData({
          cnpj: requisitante.cnpj || cnpjFromStorage || '',
          num: requisitante.num || '',
          nome: requisitante.nome || '',
          responsavelNome: requisitante.responsavelNome || '',
          responsavelFuncao: requisitante.responsavelFuncao || '',
          responsavelMatricula: requisitante.responsavelMatricula || '',
          responsavelCpf: requisitante.responsavelCpf || '',
        });
        setIsEditing(true);
      } else {
        setFormData({
          cnpj: cnpjFromStorage || '',
          num: '',
          nome: '',
          responsavelNome: '',
          responsavelFuncao: '',
          responsavelMatricula: '',
          responsavelCpf: '',
        });
        setIsEditing(false);
      }
      setIsSubmitting(false);
      setError(null);
    }
  }, [isOpen, requisitante, cnpjFromStorage]);

  const validateForm = useCallback(() => {
    if (!formData.nome?.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (formData.nome.length > 100) {
      setError('Nome não pode exceder 100 caracteres');
      return false;
    }
    if (formData.responsavelNome && formData.responsavelNome.length > 100) {
      setError('Nome do responsável não pode exceder 100 caracteres');
      return false;
    }
    if (formData.responsavelFuncao && formData.responsavelFuncao.length > 50) {
      setError('Função do responsável não pode exceder 50 caracteres');
      return false;
    }
    if (
      formData.responsavelMatricula &&
      formData.responsavelMatricula.length > 20
    ) {
      setError('Matrícula do responsável não pode exceder 20 caracteres');
      return false;
    }
    if (formData.responsavelCpf && !isValidCPF(formData.responsavelCpf)) {
      setError('CPF do responsável inválido');
      return false;
    }
    setError(null);
    return true;
  }, [formData]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Limpa erro quando o usuário começa a digitar
    if (error) {
      setError(null);
    }
  };

  const handleCpfChange = e => {
    const value = unformatCPF(e.target.value);
    setFormData(prev => ({
      ...prev,
      responsavelCpf: value,
    }));
    // Limpa erro quando o usuário começa a digitar
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      if (!validateForm()) return;

      setIsSubmitting(true);
      setError(null);

      try {
        const dataToSend = {
          ...formData,
          responsavelCpf: unformatCPF(formData.responsavelCpf),
        };
        await axios.post(`${API_BASE_URL.REQUISITANTE}/save`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (onSuccess) {
          onSuccess(formData, isEditing);
        }
        onClose();
      } catch (error) {
        console.error('Erro ao salvar requisitante:', error);
        setError(
          error.response?.data?.message ||
            'Erro ao salvar requisitante. Tente novamente.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm, token, onSuccess, onClose, isEditing]
  );

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <>
      <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
        <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
          <h6 className="modal-title text-center m-0">
            <i
              className={`bi ${
                isEditing ? 'bi-person-gear' : 'bi-person-plus'
              } me-2`}
            ></i>
            {isEditing ? 'Editar Requisitante' : 'Novo Requisitante'}
          </h6>
          <button
            className="btn btn-link text-white p-0 mb-0"
            onClick={handleClose}
            title="Fechar"
            style={{ fontSize: '0.9rem', textDecoration: 'none' }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Form.Group className="mb-1">
              <Form.Label>Nome *</Form.Label>
              <Form.Control
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                required
                maxLength={100}
                placeholder="Nome do requisitante"
              />
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>Nome do Responsável</Form.Label>
              <Form.Control
                type="text"
                name="responsavelNome"
                value={formData.responsavelNome}
                onChange={handleInputChange}
                maxLength={100}
                placeholder="Nome do responsável"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>CPF do Responsável</Form.Label>
                  <CPFMaskInput
                    value={formatCPF(formData.responsavelCpf)}
                    onChange={handleCpfChange}
                    placeholder="000.000.000-00"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>Função do Responsável</Form.Label>
                  <Form.Control
                    type="text"
                    name="responsavelFuncao"
                    value={formData.responsavelFuncao}
                    onChange={handleInputChange}
                    maxLength={50}
                    placeholder="Função/Cargo"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>Matrícula do Responsável</Form.Label>
                  <Form.Control
                    type="text"
                    name="responsavelMatricula"
                    value={formData.responsavelMatricula}
                    onChange={handleInputChange}
                    maxLength={20}
                    placeholder="Matrícula"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="text-muted">
              <small>* Campos obrigatórios</small>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Salvando...
                </>
              ) : isEditing ? (
                'Atualizar'
              ) : (
                'Salvar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

RequisitanteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  requisitante: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default RequisitanteModal;
